import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from './auth';

/**
 * VERAX: Verificación de los casos de junio a septiembre
 *
 * Lo que el sistema observó y produjo al verificar, con sus propios motores y
 * en una fecha real, cada dominio registrado en el periodo: si sigue en línea,
 * cuándo emitió su primer certificado (el momento en que el monitor lo habría
 * visto), si las reglas lo reconocen, qué decidió la validación automática y
 * qué borradores de baja quedaron esperando aprobación.
 */

interface Bloque {
  casos: number; dominiosPropios: number; resuelvenHoy: number; activosHoy: number; retiradosVerificados: number;
  reconocidosPorReglas: number; reconocidosSoloMarca: number; certificadoRenovado?: number; conFechaDeteccion: number; ventajaMedianaDias: number | null;
  ventajaMinimaMedianaDias: number | null; confirmadosHoy: number; paraRevision: number; borradoresBaja: number;
}
interface Resultado {
  id: string; domain: string; url: string; period: string; priorStatus: string; dnsResolves: boolean;
  httpStatus: number | null; active: boolean; registryStatus: string | null; registrar: string | null;
  registeredAt: string | null; firstCertAt: string | null; certIssuer: string | null; certCount: number;
  matchReason: string | null; detectableAt: string | null; leadDaysMin: number | null; leadDaysReport: number | null;
  decision: string | null; criteria: { identity?: string[]; harm?: string[]; note?: string } | null;
  reportsDrafted: number; error: string | null; checkedAt: string;
}
interface Run {
  id: string; startedAt: string; finishedAt: string; host: string | null; rulesVersion: string;
  periodFrom: string; periodTo: string; totals: { total: Bloque; meses: (Bloque & { mes: string })[] };
  results: Resultado[]; company: { name: string; domain: string };
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const mesLabel = (iso: string) => { const [y, m] = iso.slice(0, 7).split('-').map(Number); return `${MESES[m - 1]} ${y}`; };
const fecha = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: '2-digit' }) : '-';

const REGLA: Record<string, string> = {
  BRAND_SUBSTRING: 'marca en el dominio', EDIT_DISTANCE: 'marca con error', BRAND_FUZZY: 'marca escrita con error',
  SECTOR_WATCH: 'vigilancia sectorial'
};
const DECISION: Record<string, { t: string; c: string }> = {
  CONFIRMED: { t: 'Confirmado', c: 'bg-red-900/60 text-red-300' },
  NEEDS_REVIEW: { t: 'Para revisión', c: 'bg-amber-900/50 text-amber-300' },
  WATCH: { t: 'En vigilancia', c: 'bg-sky-900/50 text-sky-300' },
  INACTIVE: { t: 'Fuera de línea', c: 'bg-emerald-900/40 text-emerald-300' },
  DISMISSED: { t: 'Descartado', c: 'bg-gray-800 text-gray-400' }
};

const Tile: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
    <p className="text-xs text-gray-400">{label}</p>
    <p className="text-3xl font-bold mt-1 tabular-nums">{value}</p>
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

export const VerificationPanel: React.FC<{ onReportes?: () => void }> = ({ onReportes }) => {
  const [run, setRun] = useState<Run | null | undefined>(undefined);
  const [reportes, setReportes] = useState<Record<string, number>>({});
  const [error, setError] = useState('');
  const [mes, setMes] = useState<string | null>(null);
  const [solo, setSolo] = useState<'TODOS' | 'ACTIVOS' | 'DETECTABLES'>('TODOS');

  useEffect(() => {
    axios.get(`${API}/replay`)
      .then(r => { setRun(r.data.run); setReportes(r.data.reportes ?? {}); })
      .catch(e => setError(e?.response?.data?.error || 'No se pudo cargar la verificación'));
  }, []);

  const filas = useMemo(() => (run?.results ?? [])
    .filter(r => !mes || r.period.slice(0, 7) === mes)
    .filter(r => solo === 'TODOS' || (solo === 'ACTIVOS' ? r.active : !!r.detectableAt)), [run, mes, solo]);

  if (error) return <p className="text-red-400">{error}</p>;
  if (run === undefined) return <p className="text-gray-500">Cargando verificación…</p>;
  if (run === null) return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-gray-300">
      <p className="font-semibold mb-2">Todavía no hay una ejecución de verificación.</p>
      <p className="text-sm text-gray-400">
        Se corre desde el equipo donde está instalado VERAX, con salida a internet:
      </p>
      <pre className="bg-gray-950 border border-gray-800 rounded p-3 mt-3 text-xs">npm run ejercicio:verificacion</pre>
      <p className="text-xs text-gray-500 mt-3">
        Verifica cada dominio de junio a septiembre (DNS, RDAP, Certificate Transparency y, si sigue en línea,
        navegador y validación automática) y deja los borradores de baja esperando aprobación.
      </p>
    </div>
  );

  const t = run.totals.total;
  const pendientes = reportes.DRAFT ?? 0;

  return (
    <div>
      <div className="flex justify-between items-start mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Verificación de casos</h2>
          <p className="text-sm text-gray-400">
            {run.company.name} · {mesLabel(run.periodFrom)} a {mesLabel(run.periodTo)} · ejecutada el {fecha(run.startedAt)}
            {run.host && <> en <span className="font-mono">{run.host}</span></>}
          </p>
          <p className="text-[11px] text-gray-500 mt-0.5">Reglas {run.rulesVersion}</p>
        </div>
        {pendientes > 0 && (
          <button onClick={onReportes} className="bg-amber-700 hover:bg-amber-600 text-sm px-4 py-2 rounded">
            {pendientes} solicitudes esperan aprobación →
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Tile label="Dominios verificados" value={t.casos} hint={`${t.dominiosPropios} propios · ${t.casos - t.dominiosPropios} en plataformas`} />
        <Tile label="Siguen en línea" value={<span style={{ color: t.activosHoy ? '#f87171' : '#34d399' }}>{t.activosHoy}</span>}
          hint={`${t.retiradosVerificados} fuera de línea, verificado por VERAX`} />
        <Tile label="Reconocidos por las reglas" value={`${t.reconocidosPorReglas}/${t.dominiosPropios}`}
          hint={`${t.reconocidosSoloMarca} por la marca · ${t.reconocidosPorReglas - t.reconocidosSoloMarca} por vigilancia sectorial`} />
        <Tile label="Ventaja mediana" value={t.ventajaMedianaDias !== null ? <>{t.ventajaMedianaDias}<span className="text-base text-gray-400"> días</span></> : '-'}
          hint={`del primer certificado a la notificación · n=${t.conFechaDeteccion}${t.certificadoRenovado ? ` (${t.certificadoRenovado} excluidos: certificado renovado)` : ''}`} />
        <Tile label="Confirmados por VERAX" value={t.confirmadosHoy} hint={`${t.borradoresBaja} borradores de baja · ${t.paraRevision} para revisión`} />
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 mb-5 overflow-x-auto">
        <h4 className="text-sm font-semibold mb-3">Por mes</h4>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500">
            <th className="pb-2">Mes</th><th>Dominios</th><th>En línea hoy</th><th>Reconocidos</th>
            <th>Ventaja mediana</th><th>Confirmados</th><th>Borradores</th>
          </tr></thead>
          <tbody>
            {run.totals.meses.map(m => (
              <tr key={m.mes} onClick={() => setMes(mes === m.mes ? null : m.mes)}
                className={`border-t border-gray-800 cursor-pointer ${mes === m.mes ? 'bg-cyan-950/40' : 'hover:bg-gray-800/40'}`}>
                <td className="py-2 capitalize">{mesLabel(m.mes)}</td>
                <td className="tabular-nums">{m.casos}</td>
                <td className="tabular-nums">{m.activosHoy}</td>
                <td className="tabular-nums">{m.reconocidosPorReglas}/{m.dominiosPropios}</td>
                <td className="tabular-nums">{m.ventajaMedianaDias !== null ? `${m.ventajaMedianaDias} días` : '-'}</td>
                <td className="tabular-nums">{m.confirmadosHoy}</td>
                <td className="tabular-nums">{m.borradoresBaja}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <h4 className="text-sm font-semibold">Resultado por dominio {mes && <span className="text-gray-400 font-normal capitalize">· {mesLabel(mes)}</span>}</h4>
          <div className="flex gap-1 text-xs" role="group" aria-label="Filtro">
            {([['TODOS', 'Todos'], ['ACTIVOS', 'En línea'], ['DETECTABLES', 'Con fecha de detección']] as const).map(([v, l]) => (
              <button key={v} onClick={() => setSolo(v)} className={`px-2 py-1 rounded ${solo === v ? 'bg-gray-700' : 'text-gray-400'}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500">
              <th className="pb-2 pr-3">Dominio</th><th className="pr-3">Mes</th><th className="pr-3">Hoy</th>
              <th className="pr-3">Registro</th><th className="pr-3">Primer certificado</th><th className="pr-3">Regla</th>
              <th className="pr-3">Ventaja</th><th>Validación automática</th>
            </tr></thead>
            <tbody>
              {filas.map(r => {
                const d = r.decision ? DECISION[r.decision] : null;
                const motivos = [...(r.criteria?.identity ?? []), ...(r.criteria?.harm ?? [])];
                return (
                  <tr key={r.id} className="border-t border-gray-800 align-top">
                    <td className="py-2 pr-3 font-mono text-xs break-all">{r.domain}</td>
                    <td className="pr-3 text-xs capitalize whitespace-nowrap">{mesLabel(r.period).split(' ')[0]}</td>
                    <td className="pr-3 text-xs whitespace-nowrap">
                      {r.active ? <span className="text-red-400">en línea{r.httpStatus ? ` (${r.httpStatus})` : ''}</span>
                        : <span className="text-emerald-400">{r.dnsResolves ? 'sin contenido' : 'no resuelve'}</span>}
                    </td>
                    <td className="pr-3 text-[11px] text-gray-400">
                      {r.registryStatus ? (/hold/i.test(r.registryStatus) ? 'suspendido' : r.registryStatus === 'NO REGISTRADO' ? 'liberado' : r.registryStatus.split(',')[0]) : '-'}
                      {r.registrar && <span className="block text-gray-500">{r.registrar}</span>}
                    </td>
                    <td className="pr-3 text-xs whitespace-nowrap">{fecha(r.firstCertAt)}{r.certIssuer && <span className="block text-[10px] text-gray-500">{r.certIssuer}</span>}</td>
                    <td className="pr-3 text-xs">{r.matchReason ? REGLA[r.matchReason] ?? r.matchReason : <span className="text-gray-600">ninguna</span>}</td>
                    <td className="pr-3 text-xs tabular-nums whitespace-nowrap">{r.leadDaysReport === null ? '-' : r.leadDaysReport < 0 ? <span title="El primer certificado visible es posterior a la notificación: es una renovación y el original ya no aparece." className="text-gray-500">renovado</span> : `${r.leadDaysReport} días`}</td>
                    <td className="text-xs">
                      {d ? <span className={`px-2 py-0.5 rounded ${d.c}`}>{d.t}</span> : <span className="text-gray-600">-</span>}
                      {r.reportsDrafted > 0 && <span className="ml-2 text-amber-300">{r.reportsDrafted} borradores</span>}
                      {motivos.length > 0 && <ul className="mt-1 text-[11px] text-gray-400 list-disc ml-4">{motivos.map(m => <li key={m}>{m}</li>)}</ul>}
                      {r.criteria?.note && <p className="mt-1 text-[11px] text-gray-500">{r.criteria.note}</p>}
                      {r.error && <p className="mt-1 text-[11px] text-amber-500">{r.error}</p>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-gray-600 mt-3">
          "Ventaja" son los días entre el primer certificado público del dominio (cuando el monitor de VERAX lo habría
          alertado) y la notificación del caso a la empresa; solo se cuenta cuando una regla vigente reconoce el nombre.
          "Hoy" es lo que VERAX observó en la fecha de la ejecución. Ninguna solicitud se envía sin aprobación de un ADMIN.
        </p>
      </div>
    </div>
  );
};
